import { useEffect, useState, useCallback } from 'react';
import { Alert } from '@mui/material';
import CircularProgress from '@mui/material/CircularProgress';
import { Ads, useAds } from 'api/ads';
import { Box, Button, CustomBox, GridChild, GridContainer, Title } from 'common';

import ImageNotSupportedIcon from '@mui/icons-material/ImageNotSupported';

import { Helmet } from 'react-helmet';

import dayjs from 'dayjs';
import { useParams } from 'react-router-dom';
import { AdsCard } from 'widgets/cards';

import { useLanguage } from '../../context/LanguageContext';
import { singleAdsLabels } from '../../locales/translations';

import { AdsImagesCarousel } from './AdsImagesCarousel';

export const SingleAdsPage = () => {
    const { id } = useParams();
    const { onGetAllAds, onGetOneAds } = useAds();

    const [adsDetails, setAdsDetails] = useState<Ads | null>(null);
    const [latestAds, setLatestAds] = useState<Ads[]>([]);
    const [loadingDetails, setLoadingDetails] = useState(true);
    const [loadingLatestNews, setLoadingLatestNews] = useState(true);
    const [error, setError] = useState<string>('');

    const { language } = useLanguage();

    const fetchAdsDetails = useCallback(async () => {
        try {
            const result = await onGetOneAds(Number(id));
            if (result.success) {
                setAdsDetails(result.data);
            } else {
                setError('Не удалось загрузить детали объявления.');
            }
        } catch (error) {
            setError('Произошла ошибка при запросе деталей объявления.');
        } finally {
            setLoadingDetails(false);
        }
    }, [id, onGetOneAds]);

    const fetchLatestAds = useCallback(async () => {
        try {
            const result = await onGetAllAds();
            if (result.success) {
                setLatestAds(result.data);
            } else {
                setError('Произошла ошибка при загрузке списка объявлений.');
            }
        } catch (error) {
            setError('Произошла ошибка при запросе списка объявлений.');
        } finally {
            setLoadingLatestNews(false);
        }
    }, [onGetAllAds]);

    useEffect(() => {
        fetchLatestAds();
        fetchAdsDetails();
    }, [fetchLatestAds, fetchAdsDetails]);


    if (loadingDetails && loadingLatestNews)
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                <CircularProgress />
            </Box>
        );

    if (error) return <Alert severity="error">{error}</Alert>;

    return (
        <Box>
            <GridContainer>
                <GridChild md={8}>
                    {adsDetails && (
                        <CustomBox title={adsDetails[`title_${language}`]}>
                            <Helmet>
                                <title>{adsDetails[`title_${language}`]}</title>
                                <meta name="description" content="Информация о библиотеке AlmaU Open Library" />
                            </Helmet>
                            {adsDetails.images && adsDetails.images.length > 0 && adsDetails.images[0] ? (
                                <AdsImagesCarousel adsItem={adsDetails} />
                            ) : (<ImageNotSupportedIcon
                                sx={{
                                    height: 200,
                                    width: '100%',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    fontSize: '5rem'
                                }}
                            />)}
                            <Title gutterBottom variant="h5" sx={{ fontWeight: 600, marginTop: 2 }}>
                                {adsDetails[`title_${language}`]}
                            </Title>
                            <Title gutterBottom variant="body2" color="text.secondary">
                                {adsDetails[`description_${language}`]}
                            </Title>
                            <Title variant="body2" color="text.primary">
                                {dayjs(adsDetails.created_at).format('DD.MM.YYYY')}
                            </Title>
                        </CustomBox>
                    )}
                </GridChild>

                <GridChild md={4}>
                    {!loadingLatestNews && (
                        <CustomBox title={singleAdsLabels.latestAds[language]}>
                            {latestAds.slice(0, 3).map((adsItem) => (
                                <AdsCard key={adsItem.id} adsItem={adsItem} />
                            ))}
                            <Box sx={{ paddingTop: 6, display: 'flex', justifyContent: 'center' }}>
                                <Button variant="contained" color="error" href="/ads">
                                    {singleAdsLabels.allAdsButton[language]}
                                </Button>
                            </Box>
                        </CustomBox>
                    )}
                </GridChild>
            </GridContainer>
        </Box>
    );
};
